// Exit-intent/scroll micro-survey (DF-104), generalized for reuse on other
// routes (DF-240 investigation, 2026-10-01).
//
// Purpose: we have almost no direct user feedback (see CLAUDE.md — 85
// visitors/30d, 0 signups). This asks the single highest-value question —
// what's stopping a visitor from starting a mission page — and records the
// answer as a PostHog custom event.
//
// No backend involved on purpose: every Firestore collection currently
// requires isSignedIn() (firestore.rules), and firestore.rules/functions
// deploys are human-gated (DF-92) — a new collection would silently 550 for
// every anonymous visitor until a human runs the deploy. PostHog capture is
// already live for anonymous visitors with no deploy dependency, so answers
// land in the SAME "micro_survey_response" event queryable next run.
//
// Trigger: 40% scroll depth (any device) OR 90s dwell time, whichever fires
// first — once per tab session per `page` key (sessionStorage is UI state,
// not tracking/persistence, so this does not require consent per the same
// reasoning as ConsentBanner). The exit-intent (mouseleave-at-top) trigger
// was removed 2026-10-05 — see the dated note below.
//
// Tuned 2026-08-25: the original 60%-scroll-only threshold collected ZERO
// responses in the 90 days since DF-104 shipped (confirmed via
// `micro_survey_response` in PostHog), despite avg. homepage dwell time of
// ~132s ($pageleave $prev_pageview_duration) — most visitors are reading,
// not scrolling past the fold, so the trigger was never firing for them. The
// dwell-time fallback and lower scroll bar are aimed at that gap, not at
// making the popup more aggressive.
//
// Tuned again 2026-10-05: 11/11 micro_survey_response events over the 21
// days since were '__dismissed__' — never a real answer — which the
// 2026-10-04 run traced to active rejection (the explicit X), not
// non-engagement. The mouseleave-at-top listener had zero minimum delay, so
// it could fire within the first second or two for anyone whose cursor
// merely passed near the address bar/tab strip while the page was still
// loading — an intrusive popup with no relevance yet, primed to be
// dismissed on reflex. Removed that trigger and pushed the dwell fallback
// 45s->90s so the survey never appears before a visitor has had a real
// chance to read something. Scroll-depth trigger (40%) is untouched.
//
// Generalized 2026-10-01 (DF-240): /churches drives ~100% of real traffic
// (893 pageviews 2026-09-28..30) but its creator-acquisition CTAs
// (`church_creator_cta_click`) had fired ZERO times over that same window —
// a real sample, not "too early to tell." That's consistent with either a
// weak CTA or an audience mismatch (church-info seekers vs. would-be
// missionaries), and nothing distinguishes the two without asking. This
// component now takes its question/reasons/page as props instead of being
// homepage-only, so the SAME zero-backend mechanism can run on
// /churches/:id to test the audience-mismatch theory directly.

import { useEffect, useRef, useState } from 'react';
import { posthog } from '@/lib/analytics/posthog';

const SESSION_KEY_PREFIX = 'df_micro_survey_shown';

// ConsentBanner moved to `fixed inset-x-0 top-0` on 2026-10-08 (was
// bottom-0, which collided with bottom-fixed widgets like this one and, on
// some pages, the donate CTA itself -- see ConsentBanner.tsx). Nothing at
// the bottom of the viewport needs to dodge it any more.
const DEFAULT_QUESTION = "What's stopping you from starting a mission page?";

const DEFAULT_REASONS = [
  "Not sure it's for me",
  'Worried about fees',
  'Need to see it work first',
  "Don't have a mission/ministry yet",
  'Just browsing',
];

function isPrerender(): boolean {
  return (
    typeof window === 'undefined' ||
    (window as unknown as { __PRERENDER_INJECTED?: boolean })
      .__PRERENDER_INJECTED === true
  );
}

interface MicroSurveyProps {
  question?: string;
  reasons?: string[];
  /** Recorded on the event and used to namespace the once-per-session key. */
  page?: string;
}

export default function MicroSurvey({
  question = DEFAULT_QUESTION,
  reasons = DEFAULT_REASONS,
  page = '/',
}: MicroSurveyProps = {}) {
  const SESSION_KEY = `${SESSION_KEY_PREFIX}:${page}`;
  const [visible, setVisible] = useState(false);
  const [answered, setAnswered] = useState(false);
  const firedRef = useRef(false);

  useEffect(() => {
    if (isPrerender()) return;
    try {
      if (sessionStorage.getItem(SESSION_KEY)) return;
    } catch {
      /* sessionStorage unavailable (e.g. private mode) — skip gracefully */
    }

    function trigger() {
      if (firedRef.current) return;
      firedRef.current = true;
      setVisible(true);
      try {
        sessionStorage.setItem(SESSION_KEY, '1');
      } catch {
        /* ignore */
      }
      // DF-240: without this, we only ever learn about an impression when the
      // visitor explicitly clicks a reason or the × — a visitor who just
      // navigates away leaves no trace, so "0 responses" was indistinguishable
      // from "nobody saw it." This fires the moment the widget renders, so
      // shown vs. answered vs. dismissed can finally be told apart.
      try {
        posthog.capture('micro_survey_shown', { question, page });
      } catch {
        /* posthog not initialised — nothing to record */
      }
    }

    function onScroll() {
      const scrollable = document.documentElement.scrollHeight - window.innerHeight;
      if (scrollable <= 0) return;
      if (window.scrollY / scrollable >= 0.4) trigger();
    }

    const dwellTimer = window.setTimeout(trigger, 90_000);

    window.addEventListener('scroll', onScroll, { passive: true });
    return () => {
      window.clearTimeout(dwellTimer);
      window.removeEventListener('scroll', onScroll);
    };
  }, []);

  if (!visible) return null;

  function respond(reason: string) {
    try {
      posthog.capture('micro_survey_response', {
        question,
        answer: reason,
        page,
      });
    } catch {
      /* posthog not initialised — nothing to record */
    }
    setAnswered(true);
    window.setTimeout(() => setVisible(false), 1800);
  }

  function dismiss() {
    try {
      posthog.capture('micro_survey_response', {
        question,
        answer: '__dismissed__',
        page,
      });
    } catch {
      /* ignore */
    }
    setVisible(false);
  }

  return (
    <div
      role="dialog"
      aria-live="polite"
      aria-label="Quick feedback"
      className="fixed bottom-4 right-4 z-[90] w-[min(20rem,calc(100vw-2rem))] rounded-lg border border-slate-200 bg-white p-4 shadow-lg"
    >
      {answered ? (
        <p className="text-sm text-slate-700">Thanks — that helps us build the right thing. 🙏</p>
      ) : (
        <>
          <div className="mb-2 flex items-start justify-between gap-2">
            <p className="text-sm font-medium text-slate-800">{question}</p>
            <button
              type="button"
              onClick={dismiss}
              aria-label="Close"
              className="shrink-0 text-slate-400 hover:text-slate-600"
            >
              ×
            </button>
          </div>
          <div className="flex flex-col gap-1.5">
            {reasons.map((reason) => (
              <button
                key={reason}
                type="button"
                onClick={() => respond(reason)}
                className="rounded-md border border-slate-200 px-3 py-1.5 text-left text-xs text-slate-700 hover:border-custom-blue hover:bg-slate-50"
              >
                {reason}
              </button>
            ))}
          </div>
        </>
      )}
    </div>
  );
}
